import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Radio, Row, Select, Space } from 'antd';
import dayjs from 'dayjs';

import styles from './index.module.scss';
import { cssList } from '../../../utils/csslist';
import { useDivisionSelector } from '../../components/division_selector/interface';
import { useSessionStore } from '../../../context/session';
import { CsvListRequest } from '../../../model/csvlist_request';
import {
  businessTypeToDataType,
  CsvListDataType,
  DataTypeKeys,
  dataTypeToBusinessType,
  DateType,
  DateTypeKeys,
  SourceDisplayForCleaning,
  SourceDisplayForEquipment,
  SourceDisplayForGreening,
  SourceDisplayForRequest,
  SourceKeys,
  sourceKeysToBusinessType,
  sourceKeysToDataType,
} from '../../../constant';
import { PermissionName } from '../../../valueobject/permissionName';

/**
 * 検索エリアのプロパティ
 */
type Props = {
  onSearch: (type: 'search' | 'download', param: CsvListRequest) => void;
};

/**
 * 検索エリア
 * @param onSearch
 * @constructor
 */
export const TaskEditSearchPanel: React.FC<Props> = ({ onSearch }) => {
  // Form入力値の状態管理
  const [param, setParam] = useState<CsvListRequest>();
  // 対象期間(年)フラグ
  const [isYear, setIsYear] = useState(true);
  // 対象期間(月)設定値
  const [preMonth, setPreMonth] = useState(0);
  // 対象ファイルの状態管理
  const [dataTypeOptions, setDataTypeOptions] = useState<{ value: SourceKeys; label: string }[]>(SourceDisplayForCleaning);
  // ログイン情報
  const { loginInfo, hasPermission } = useSessionStore((selector) => selector);

  // const [data, setData] = useState<TaskEditRequest>();
  const [height, setHeight] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [isSearchBox, setIsSearchBox] = useState(true);
  const [yearLabel, setYearLabel] = useState('');
  const ref = useRef<HTMLDivElement>();
  const { DivisionSelector } = useDivisionSelector();

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
      setOriginalHeight(ref.current.scrollHeight);
    }
  }, [onSearch]);

  const nowyear = useMemo(() => {
    const now = dayjs();
    if (now.month() < 4) {
      return now.year() - 1;
    }
    return now.year();
  }, []);

  useEffect(() => {
    if (param?.dateType === DateTypeKeys.yearly) {
      setYearLabel('対象年度');
      setParam((param) => ({ ...param, year: nowyear }));
    } else {
      setYearLabel('対象年');
      setParam((param) => ({ ...param, year: dayjs().year() }));
    }
  }, [param?.dateType]);


  const getSourceKeys = useCallback((dataType: DataTypeKeys) => {
    const keys = () => {
      switch (dataType) {
        case DataTypeKeys.dataTypeCleaning:
          return SourceDisplayForCleaning;
        case DataTypeKeys.dataTypeGreening:
          return SourceDisplayForGreening;
        case DataTypeKeys.dataTypeEquipment:
          return SourceDisplayForEquipment;
        case DataTypeKeys.dataTypeRequest:
          return SourceDisplayForRequest;
        default:
          return SourceDisplayForCleaning;
      }
    };
    return keys().filter((t) => {
      if (dataType !== DataTypeKeys.dataTypeRequest) {
        return true;
      }
      if (hasPermission(PermissionName.ALL_WORK_PLAN)) {
        return true;
      }
      return sourceKeysToBusinessType(t.value) === loginInfo?.user?.company?.businessType;
    });
  }, [hasPermission, loginInfo]);

  useEffect(() => {
    // 初期値設定
    const data = (() => {
      if (hasPermission(PermissionName.ALL_WORK_PLAN)) {
        return DataTypeKeys.dataTypeCleaning;
      }
      return businessTypeToDataType(loginInfo?.user?.company?.businessType);
    })();
    const defaultFiles = getSourceKeys(data);
    setPreMonth(dayjs().month() + 1);
    setDataTypeOptions(defaultFiles);
    setParam((param) => {
      return {
        ...param,
        targetFile: defaultFiles[0].value,
        divisionId: 0,
        floorId: 0,
        blockId: 0,
        dateType: DateTypeKeys.yearly,
        year: nowyear,
        month: 0,
      };
    });
  }, []);

  /** 業務データ種別 */
  const csvListTypeOptions = useMemo(() => {
    return [
      ...CsvListDataType.filter((type) => {
        return hasPermission(PermissionName.ALL_WORK_PLAN) || type.key === DataTypeKeys.dataTypeRequest || dataTypeToBusinessType(type.key) === loginInfo?.user?.company?.businessType;
      }).map((type) => {
        return { value: type.key, label: type.value };
      }),
    ];
  }, []);

  const dataType = useMemo(() => {
    switch (param?.targetFile) {
      case SourceKeys.cleaning_daily:
      case SourceKeys.cleaning_reg:
        return DataTypeKeys.dataTypeCleaning;
      case SourceKeys.greening_daily:
      case SourceKeys.greening_reg:
        return DataTypeKeys.dataTypeGreening;
      case SourceKeys.btsas:
        return DataTypeKeys.dataTypeEquipment;
      case SourceKeys.request_all:
      case SourceKeys.request_cleaning:
      case SourceKeys.request_greening:
      case SourceKeys.request_equipment:
      case SourceKeys.request_security:
        return DataTypeKeys.dataTypeRequest;
      default:
        if (hasPermission(PermissionName.ALL_WORK_PLAN)) {
          return DataTypeKeys.dataTypeCleaning;
        }
        return businessTypeToDataType(loginInfo?.user?.company?.businessType);
    }
  }, [param?.targetFile, loginInfo, hasPermission]);

  /** 業務データ種別プルダウン変更時 */
  const handleBusinessType = (value:DataTypeKeys) => {
    const sourceKeys = getSourceKeys(value);
    setDataTypeOptions(sourceKeys);
    setParam((param) => {
      return { ...param, targetFile: sourceKeys[0].value };
    });
  };

  /** 年次 */
  const now = dayjs();
  const yearOptions = [
    ...Array.from({ length: 6 }, (_, i) => {
      return { value: `${now.year() - (i - 1)}`, label: `${now.year() - (i - 1)}` };
    }),
  ];

  /** 月次 */
  const monthOptions = [
    ...Array.from({ length: 12 }, (_, i) => {
      return { value: `${i + 1}`, label: `${i + 1}` };
    }),
  ];

  /** 対象期間プルダウン変更時 */
  const handleDateType = (value:DateTypeKeys) => {
    if (value === DateTypeKeys.yearly) {
      // 年次
      setIsYear(true);
      setParam((param) => {
        return { ...param, dateType: value, month: 0 };
      });
    } else {
      // 月次
      setIsYear(false);
      setParam((param) => {
        return { ...param, dateType: value, month: preMonth };// 月次プルダウンに設定していた内容を再設定
      });
    }
  };

  /** 月次プルダウン変更時 */
  const handleMonth = (value:number) => {
    setPreMonth(value);
    setParam((param) => {
      return { ...param, month: value };
    });
  };

  useEffect(() => {
    if (param?.targetFile === SourceKeys.cleaning_daily || param?.targetFile === SourceKeys.greening_daily || param?.targetFile === SourceKeys.btsas) {
      // 月次
      setIsYear(false);
      setParam((param) => {
        return { ...param, dateType: DateTypeKeys.monthly, month: preMonth };
      });
    } else {
      // 年間
      setIsYear(true);
      setParam((param) => {
        return { ...param, dateType: DateTypeKeys.yearly, month: 0 };
      });
    }
  }, [param?.targetFile]);

  const handleShowSearchButton = () => {
    setIsSearchBox(!isSearchBox);
    const h = isSearchBox ? 40 : originalHeight;
    setHeight(h);
  };

  const handleSearch = () => {
    onSearch('search', param);
  };

  const handleDownload = () => {
    onSearch('download', param);
  };

  const style = useMemo(() => {
    if (height === 0) {
      return {};
    }
    return { height };
  }, [height]);

  const onChangeFile = (value: SourceKeys) => {
    setParam((param) => {
      return { ...param, targetFile: value };
    });
  };

  return (
      <div ref={ref} style={style}
        className={cssList(styles.section, styles.relative, styles.searchBox, { [styles.hide]: !isSearchBox })}>
        <span className={styles.menuShowBtn}>
          <Button type={'dashed'} size={'small'} onClick={handleShowSearchButton} style={{ color: 'gray' }}>{(isSearchBox ?? true) ? '検索条件 非表示' : '検索条件 表示'}</Button>
        </span>
        <div>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              対象業務
            </div>
            <Col span={4}>
              <Select
                  style={{ width: 120 }}
                  value={dataType}
                  options={csvListTypeOptions}
                  onChange={handleBusinessType}
              />
            </Col>
            <div className={styles.formLabel}>
            対象ファイル
            </div>
            <Col>
              <Select
                  style={{ width: 280 }}
                  value={param?.targetFile ?? dataTypeOptions[0]}
                  options={dataTypeOptions}
                  onChange={onChangeFile}
              />
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              {yearLabel}
            </div>
            <Col span={4}>
              <Select
                  style={{ width: 120 }}
                  value={param?.year ?? nowyear}
                  options={yearOptions}
                  onChange={(value) => setParam((param) => {
                    return { ...param, year: value };
                  })}
              />
            </Col>
            <div className={styles.formLabel}>
              対象期間
            </div>
            <Col>
              <Space>
                <Radio.Group
                  value={param?.dateType ?? DateTypeKeys.yearly}
                  onChange={(e) => handleDateType(e.target.value)}
                >
                  {DateType.map((type) => (
                    <Radio key={type.key} value={type.key} disabled={(dataTypeOptions.filter((s) => sourceKeysToDataType(s.value) === DataTypeKeys.dataTypeRequest).length === 0) && (type.key !== param?.dateType)}>
                      {type.value}
                    </Radio>
                  ))}
                </Radio.Group>
                <Select
                  disabled={isYear}
                  style={{ width: 120 }}
                  defaultValue={dayjs().month() + 1}
                  options={monthOptions}
                  onChange={(value) => handleMonth(value)}
                />
              </Space>
            </Col>
          </Row>
          <DivisionSelector
            defaultDivisionId={param?.divisionId ?? 0}
            defaultFloorId={param?.floorId ?? 0}
            defaultBlockId={param?.blockId ?? 0}
            onChange={({ divisionId, floorId, blockId }) => {
              setParam({ ...param, divisionId, floorId, blockId });
            }}
          />
          <Row className={styles.row}>
            <Space>
              <Button type="primary" onClick={handleSearch}>検索</Button>
              <Button type="primary" onClick={handleDownload}>ダウンロード</Button>
            </Space>
          </Row>
        </div>
      </div>
  );
    
};
